<template>
  <ArtistPublicProfile v-if="artistSlug" :slug="artistSlug" />

  <!-- Psyfeature: custom layout -->
  <LandingPsyfeatureLanding v-else-if="tenant?.slug === 'psyfeature'" />

  <!-- Dynamic landing page from builder -->
  <LandingRenderer v-else-if="landingConfig?.blocks?.length" :blocks="landingConfig.blocks" />

  <!-- Fallback: hardcoded landing page -->
  <div v-else class="landing-page">
    <ClientOnly v-if="isDiztree">
      <DiztreeIntroOverlay />
    </ClientOnly>
    <LandingNav />
    <!-- Hero opts into the conversion extras (secondary CTA, fact chips,
         trustline) that builder-tenant heroes don't get by default. -->
    <LandingHero
      :secondary-cta-text="$t('landing.hero_cta_secondary')"
      secondary-cta-link="#how-it-works"
      :show-hero-facts="true"
      :show-trustline="true"
    />
    <!-- Social proof up front: real cross-tenant labels + artists build trust
         before the pitch. Self-hides when the showcase has no data. -->
    <LandingTrust />
    <!-- Upload → release → payout in three steps: the risk-free explanation
         a first-time visitor needs before any feature detail. -->
    <LandingHowItWorks />
    <!-- The full, updated platform story (distribute → promote → sell → get
         paid → create → run a label). Covers everything added since launch. -->
    <LandingPillars />
    <LandingFeatures />
    <!-- Mid-page conversion point so the signup path never sits a full page
         of scrolling away. -->
    <LandingCtaStrip />
    <LandingDistribution />
    <LandingShowcase />
    <LandingStats />
    <LandingPricing v-if="tenant?.subscriptionsEnabled" :showButtons="false" />
    <!-- Objection handling straight from the tenant's configured FAQ.
         Self-hides when no FAQ items exist. -->
    <LandingFaq />
    <LandingCTA />
    <AppFooter />
    <!-- Mobile-only sticky signup bar (appears after the hero, hides for
         logged-in users and while the audio player is docked). -->
    <LandingStickyCta />
    <GlobalAudioPlayer />
  </div>
</template>

<script setup>
import ArtistPublicProfile from '~/components/Artist/PublicProfile.vue'
import { getShareImageUrl } from '~/utils/proxy'
import DiztreeIntroOverlay from '~/components/DiztreeIntroOverlay.vue'

definePageMeta({
  layout: false
})

const { tenant } = useTenantTheme()
const isDiztree = computed(() => {
  const slug = tenant.value?.slug
  return !slug || slug === 'default' || slug === 'diztree'
})
const artistSlug = useState('artistSlug')
const route = useRoute()

// Fetch landing page config (published or draft preview)
const previewParam = route.query.preview === '1' ? '1' : ''
const { data: landingConfig } = await useAsyncData('landing-page-config', () => {
  if (artistSlug.value) return Promise.resolve(null)
  const params = previewParam ? { preview: '1' } : {}
  return $fetch('/api/public/landing-page', { params }).catch(() => null)
})

// SEO: use config SEO or tenant-aware defaults
if (!artistSlug.value) {
  const seo = landingConfig.value?.seo
  const tenantName = tenant.value?.theme?.appName || tenant.value?.name || 'Diztree'
  const defaultTitle = tenantName === 'Diztree'
    ? 'Diztree — Music Distribution & Audio Marketplace'
    : `${tenantName} — Music Distribution`
  const defaultDesc = tenantName === 'Diztree'
    ? 'Distribute your music to 30+ platforms, create smart shoplinks, set up download gates, and sell audio assets. All from one place.'
    : `Distribute your music with ${tenantName}. Global distribution, analytics, and more.`

  const title = seo?.title || defaultTitle
  const description = seo?.description || defaultDesc

  // Sharing/SEO extras: canonical + hreflang are built from the tenant's own
  // origin (multi-tenant — never hardcode a domain). The square app icon is
  // the og:image fallback, hence twitter:card "summary" not "summary_large_image".
  const url = useRequestURL()
  const origin = url.origin
  const switchLocalePathFn = useSwitchLocalePath()
  const canonical = origin + url.pathname
  const enHref = origin + (switchLocalePathFn('en') || '/')
  const deHref = origin + (switchLocalePathFn('de') || '/de')
  const ptHref = origin + (switchLocalePathFn('pt') || '/pt')
  const esHref = origin + (switchLocalePathFn('es') || '/es')
  // Proxied + absolute, same reason as the storefront layout: the landing
  // builder stores the raw object URL and the bucket behind it need not be
  // public. The icon fallback is already on our own origin, so it passes
  // through untouched.
  const ogImage = getShareImageUrl(seo?.ogImage, origin) || `${origin}/android-chrome-512x512.png`

  useHead({
    title,
    meta: [
      { name: 'description', content: description },
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: tenantName },
      { property: 'og:title', content: seo?.ogTitle || title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: canonical },
      { property: 'og:image', content: ogImage },
      { name: 'twitter:card', content: 'summary' },
      { name: 'twitter:title', content: seo?.ogTitle || title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: ogImage }
    ],
    link: [
      { rel: 'canonical', href: canonical },
      { rel: 'alternate', hreflang: 'en', href: enHref },
      { rel: 'alternate', hreflang: 'de', href: deHref },
      { rel: 'alternate', hreflang: 'pt', href: ptHref },
      { rel: 'alternate', hreflang: 'es', href: esHref },
      { rel: 'alternate', hreflang: 'x-default', href: enHref }
    ]
  })
}

useScrollReveal()
</script>

<style>
.landing-page {
  background-color: var(--bg-color);
  color: var(--text-main);
  min-height: 100vh;   /* fallback */
  min-height: 100svh;
  /* Safety net only: the 2026-08 responsive audit verified every viewport
     (320–1920 + landscape + 200% zoom) has NO horizontal overflow with this
     rule removed — real offenders were fixed at the source. It stays as a
     guard against future decorative-overflow regressions. */
  overflow-x: hidden;
}
</style>
